import React, { useMemo, useCallback, useEffect } from 'react';
import { Form, Checkbox, Slider } from 'antd';

const Filter = ({ data, skillsGroup, toShow, resetFlag }) => {
  const [form] = Form.useForm();

  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
      form.resetFields(['organizer', 'country', 'can_lead_to_certification', 'includes_exams_for_certification', 'content_type', 'duration', 'language', 'skills_group', 'timing', 'type_format']);
      toShow(safeData);
    }, [form, resetFlag, safeData, toShow]);

  const allOptions = useMemo(() => {
    const buckets = {
      organizer: [],
      country: [],
      can_lead_to_certification: [],
      includes_exams_for_certification: [],
      content_type: [],
      duration: [],
      language: [],
      skills_group: [],
      timing: [],
      type_format: [],
      price: [],
    };
    safeData.forEach((item) => {
      const push = (key, val) => {
        if (val == null) return;
        const arr = buckets[key];
        const add = (v) => v != null && !arr.includes(v) && arr.push(v);
        if (Array.isArray(val)) val.forEach(add);
        else add(val);
      };
      push('organizer', item.organizer);
      push('country', item.country);
      push('can_lead_to_certification', item.can_lead_to_certification);
      push('includes_exams_for_certification', item.includes_exams_for_certification);
      push('content_type', item.content_type);
      push('duration', item.duration);
      push('language', item.language);
      push('skills_group', item.skills_group);
      push('timing', item.timing);
      push('type_format', item.type_format);
      const p = parseFloat(item.price);
      if (!isNaN(p) && !buckets.price.includes(p)) buckets.price.push(p);
    });
    const sortArr = (arr) => arr.filter((v) => v != null).sort((a, b) => (typeof a === 'number' ? a - b : String(a).localeCompare(String(b))));
    return Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, sortArr(v)]));
  }, [safeData]);

  const maxPrice = useMemo(() => Math.max(...allOptions.price, 0), [allOptions.price]);
  const maxDuration = useMemo(() => Math.max(...allOptions.duration, 0), [allOptions.duration]);
  const minDuration = useMemo(() => Math.min(...allOptions.duration, 0), [allOptions.duration]);

  const handleValuesChange = useCallback(
    (_, values) => {
      let filtered = safeData;
      if (values.organizer?.length) filtered = filtered.filter((i) => values.organizer.includes(i.organizer));
      if (values.country?.length) filtered = filtered.filter((i) => values.country.includes(i.country));
      if (values.can_lead_to_certification?.length) filtered = filtered.filter((i) => values.can_lead_to_certification.includes(i.can_lead_to_certification));
      if (values.includes_exams_for_certification?.length) filtered = filtered.filter((i) => values.includes_exams_for_certification.includes(i.includes_exams_for_certification));
      if (values.content_type?.length) filtered = filtered.filter((i) => values.content_type.includes(i.content_type));
      if (values.duration?.length) filtered = filtered.filter((i) => i.duration >= values.duration[0] && i.duration <= values.duration[1]);
      if (values.language?.length) filtered = filtered.filter((i) => values.language.some((v) => i.language?.includes(v)));
      if (values.skills_group?.length) filtered = filtered.filter((i) => values.skills_group.some((v) => i.skills_group?.includes(v)));
      if (values.timing?.length) filtered = filtered.filter((i) => values.timing.includes(i.timing));
      if (values.type_format?.length) filtered = filtered.filter((i) => values.type_format.includes(i.type_format));
      const priceVal = values.price ?? maxPrice;
      filtered = filtered.filter((i) => (parseFloat(i.price) || 0) <= priceVal);
      toShow(filtered);
    },
    [safeData, toShow, maxPrice]
  );

  const makeOptions = (list, labelFn) => (list || []).map((v) => ({ label: labelFn ? labelFn(v) : v || 'Undefined', value: v }));

  const organizerOptions = useMemo(() => makeOptions(allOptions.organizer), [allOptions.organizer]);
  const countryOptions = useMemo(() => makeOptions(allOptions.country), [allOptions.country]);
  const canLeadOptions = useMemo(() => makeOptions(allOptions.can_lead_to_certification), [allOptions.can_lead_to_certification]);
  const includesExamsOptions = useMemo(() => makeOptions(allOptions.includes_exams_for_certification), [allOptions.includes_exams_for_certification]);
  const contentTypeOptions = useMemo(() => makeOptions(allOptions.content_type), [allOptions.content_type]);
  const langOptions = useMemo(() => makeOptions(allOptions.language), [allOptions.language]);
  const skillOptions = useMemo(() => makeOptions(allOptions.skills_group, (id) => skillsGroup.find((g) => g.id === id)?.name || id), [allOptions.skills_group, skillsGroup]);
  const timingOptions = useMemo(() => makeOptions(allOptions.timing), [allOptions.timing]);
  const typeOptions = useMemo(() => makeOptions(allOptions.type_format), [allOptions.type_format]);

  return (
    <Form form={form} initialValues={{ price: maxPrice, duration: [minDuration, maxDuration] }} layout="vertical" onValuesChange={handleValuesChange} className="filter-form">
      <Form.Item name="organizer" label="Organizer">
        <Checkbox.Group options={organizerOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="country" label="Country">
        <Checkbox.Group options={countryOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="can_lead_to_certification" label="Can Lead to Certification">
        <Checkbox.Group options={canLeadOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="includes_exams_for_certification" label="Includes Exams for Certification">
        <Checkbox.Group options={includesExamsOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="content_type" label="Content Type">
        <Checkbox.Group options={contentTypeOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="duration" label="Duration (hours)">
        <Slider
          range
          className="slider-container"
          min={minDuration}
          max={maxDuration}
          marks={{
            [minDuration]: `${minDuration}h`,
            [maxDuration]: `${maxDuration}h`,
          }}
        />
      </Form.Item>

      <Form.Item name="language" label="Language">
        <Checkbox.Group options={langOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="skills_group" label="Skills Group">
        <Checkbox.Group options={skillOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="timing" label="Timing">
        <Checkbox.Group options={timingOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="type_format" label="Type/Format">
        <Checkbox.Group options={typeOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="price" label="Max Price (€)">
        <Slider
          className="slider-container"
          min={0}
          max={maxPrice}
          marks={{
            0: '0€',
            [maxPrice]: `${maxPrice}€`,
          }}
        />
      </Form.Item>
    </Form>
  );
};

export default Filter;
